import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * Rolagem horizontal com barra em cima também.
 *
 * Uma tabela larga com 200 linhas deixa a barra de rolagem no fim da página, a
 * dois mil pixels de distância de quem está olhando o cabeçalho. A barra de
 * cima é um espelho: um div vazio com a mesma largura do conteúdo, e os dois
 * lados sincronizam o `scrollLeft`.
 *
 * A sincronização não entra em laço porque atribuir `scrollLeft` com o valor
 * que ele já tem não dispara evento de scroll.
 */
export function RolagemDupla({ children }: { children: ReactNode }) {
  const topo = useRef<HTMLDivElement>(null);
  const conteudo = useRef<HTMLDivElement>(null);
  const [largura, setLargura] = useState(0);

  useEffect(() => {
    const el = conteudo.current;
    if (el === null) return;

    const medir = () => setLargura(el.scrollWidth);
    medir();

    // A largura muda quando os dados chegam, quando o filtro corta linhas e
    // quando a janela é redimensionada.
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    const filho = el.firstElementChild;
    if (filho !== null) observador.observe(filho);

    return () => observador.disconnect();
  }, [children]);

  const precisaRolar = largura > 0 && conteudo.current !== null && largura > conteudo.current.clientWidth;

  return (
    <div>
      {precisaRolar && (
        <div
          ref={topo}
          onScroll={() => {
            if (conteudo.current !== null && topo.current !== null) {
              conteudo.current.scrollLeft = topo.current.scrollLeft;
            }
          }}
          className="mb-1 overflow-x-auto"
          aria-hidden="true"
        >
          <div style={{ width: largura, height: 1 }} />
        </div>
      )}

      <div
        ref={conteudo}
        onScroll={() => {
          if (topo.current !== null && conteudo.current !== null) {
            topo.current.scrollLeft = conteudo.current.scrollLeft;
          }
        }}
        className="overflow-x-auto"
      >
        {children}
      </div>
    </div>
  );
}
